import { useCompleteVetterJobProductionMutation, VetterJobActionKey } from '@codezo/graphql';
import { SectionHead } from '@codezo/quote-detail';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { formatDate } from '../../lib/utils';
import type { VetterJob } from './prices/price-form';
import { ORDER_ITEM_STATUS_LABEL } from './status-labels';

type OrderItem = VetterJob['orderItems'][number];

/**
 * Section 2 — once the client has ordered: the products this supplier makes.
 * Starting production is the studio's; finishing it is the supplier's.
 */
export function ProductionSection({
	orderItems,
	onChanged,
}: {
	orderItems: OrderItem[];
	onChanged: () => Promise<unknown>;
}) {
	return (
		<section className="flex flex-col gap-4">
			<SectionHead
				title="Production"
				meta="mark each product finished when it's ready to ship"
			/>
			<div className="flex flex-col divide-y divide-line-1 rounded-[var(--r-3)] border border-line-1 bg-surface">
				{orderItems.map((item) => (
					<ProductionRow key={item.id} item={item} onChanged={onChanged} />
				))}
			</div>
		</section>
	);
}

function ProductionRow({
	item,
	onChanged,
}: {
	item: OrderItem;
	onChanged: () => Promise<unknown>;
}) {
	const [confirming, setConfirming] = useState(false);
	const [complete, { loading }] = useCompleteVetterJobProductionMutation();
	const action = item.availableActions.find(
		(a) => a.key === VetterJobActionKey.CompleteProduction
	);

	const handleComplete = async () => {
		try {
			const { data } = await complete({ variables: { orderItemId: item.id } });
			const res = data?.completeVetterJobProduction;
			if (!res?.success) {
				toast.error(res?.message || 'Could not mark production finished.');
				return;
			}
			toast.success('Production marked finished');
			setConfirming(false);
			await onChanged();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not mark production finished.');
		}
	};

	return (
		<div className="flex flex-wrap items-start justify-between gap-4 px-5 py-4">
			<div className="min-w-0">
				<div className="text-base font-medium text-text-1">
					{item.productName ?? 'Unnamed product'}
				</div>
				<div className="mt-0.5 text-sm text-text-3">
					Order {item.orderNumber} · {item.quantity.toLocaleString('en-US')} pcs
				</div>
				<div className="mt-2 flex flex-wrap items-center gap-2">
					<Badge variant="outline">{ORDER_ITEM_STATUS_LABEL[item.status]}</Badge>
					{item.productionStartedAt && (
						<span className="text-sm text-text-4">
							Started {formatDate(item.productionStartedAt)}
						</span>
					)}
					{item.productionCompletedAt && (
						<span className="flex items-center gap-1 text-sm text-text-4">
							<CheckCircle2 size={12} className="text-status-success" />
							Finished {formatDate(item.productionCompletedAt)}
						</span>
					)}
				</div>
			</div>

			{/* Shown blocked with its reason rather than hidden — the supplier should
			    see what they'll be able to do and what they're waiting on. */}
			{action && (
				<div className="flex flex-col items-end gap-1.5">
					{confirming ? (
						<div className="flex gap-2">
							<Button
								variant="ghost"
								onClick={() => setConfirming(false)}
								disabled={loading}
							>
								Cancel
							</Button>
							<Button onClick={handleComplete} disabled={loading}>
								{loading && (
									<Loader2
										size={14}
										data-icon="inline-start"
										className="animate-spin"
									/>
								)}
								Confirm — it&apos;s finished
							</Button>
						</div>
					) : (
						<Button
							variant="outline"
							onClick={() => setConfirming(true)}
							disabled={!!action.blockedReason}
						>
							{action.label}
						</Button>
					)}
					{action.blockedReason && (
						<span className="max-w-64 text-right text-sm text-text-4">
							{action.blockedReason}
						</span>
					)}
				</div>
			)}
		</div>
	);
}
